import { Card, Modal, Table, Typography, Row, Col } from "antd";
import { loadJobCurrent, loadJobHistory } from "api/services/JobService";
import { TCurrentJobDetail, THistoryDetail } from "api/services/models/types";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { formatDate } from "helpers/utils";
import React, { useEffect, useState } from "react";

interface HistoryModalProps {
  isVisible: boolean;
  handleClose: () => void;
  jobId: number;
}

const detailColumns = [
  { 
    title: "User ID", 
    dataIndex: "userId", 
    key: "userId",
    width: "25%"
  },
  { 
    title: "IP Address", 
    dataIndex: "ipAddress", 
    key: "ipAddress",
    width: "25%"
  },
  { 
    title: "Event", 
    dataIndex: "eventType", 
    key: "eventType",
    width: "25%" 
  },
  {
    title: "Date & Time",
    dataIndex: "eventDatetime",
    key: "eventDatetime",
    width: "25%",
    render: (text: string) => formatDate(text+"Z")
  }
];

const HistoryModal: React.FC<HistoryModalProps> = ({ isVisible, handleClose, jobId }) => {
  const [gridData, setGridData] = useState<TCurrentJobDetail | null>(null);
  const [detailData, setDetailData] = useState<THistoryDetail[]>([]);
  const { accountId } = useTDAuthentication();

  useEffect(() => {
    const loadData = async () => {
      const gridResponse = await loadJobCurrent(jobId, accountId ?? "");
      const detailResponse = await loadJobHistory(jobId, accountId ?? "");
      const sortedDetailData = detailResponse.toSorted((a, b) => {
        return new Date(b.eventDatetime).getTime() - new Date(a.eventDatetime).getTime();
      });
      setGridData(gridResponse);
      setDetailData(sortedDetailData);
    };

    if (isVisible) {
      loadData();
    }
  }, [isVisible, jobId, accountId]);

  const handleModalClose = () => {
    setGridData(null);
    setDetailData([]);
    handleClose();
  }

  return (
    <Modal
      title="Job Details"
      open={isVisible}
      onCancel={handleModalClose}
      footer={null}
      width={800}
      key={jobId}
      maskClosable={false}
      centered={true}>
      {gridData && (
        <>
          <div>
            <Row className="job-history-card-row">
              <Col className="job-history-card-col" span={12}><strong>Job ID:</strong> {gridData.jobId}</Col>
              <Col span={12}><strong>Vf ID:</strong> {gridData.vfId}</Col>
            </Row>
            <Row className="job-history-card-row">
              <Col className="job-history-card-col" span={12}><strong>Voice File Name:</strong> {gridData.voiceFileName}</Col>
              <Col span={12}><strong>Job File Name:</strong> {gridData.docFileName}</Col>
            </Row>
            <Row className="job-history-card-row">
              <Col className="job-history-card-col" span={12}><strong>Delivered Date:</strong>{" "}{formatDate(gridData.deliveredTime.toString() + "Z")}</Col>
              <Col span={12}><strong>Received Date:</strong> {formatDate(gridData.estFileDate.toString() + "Z")}</Col>
            </Row>
            <Row className="job-history-card-row">
              <Col className="job-history-card-col" span={12}><strong>TAT:</strong> {gridData.tat}</Col>
              <Col span={12}><strong>Job Status:</strong> {gridData.jobStatus}</Col>
            </Row>
          </div>
        </>
      )}
      <Typography.Title level={3} style={{ margin: "12px 0" }}>
        Job History
      </Typography.Title>
      <Card styles={{ body: { padding: 0, maxHeight: 500 } }}>
        <Table
          sticky={true}
          dataSource={detailData}
          columns={detailColumns}
          pagination={false}
          rowKey="name"
          style={{ maxHeight: "500px", overflowY: "auto", border: "none" }}
          className="table-style"
          components={{
            body: {
              cell: (props: any) => (
                <td
                  {...props}
                  style={{
                    borderBottomWidth: "1px",
                    borderBottomColor: "#f0f0f0",
                    borderBottomStyle: "solid"
                  }}
                />
              )
            }
          }}
        />
      </Card>
    </Modal>
  );
};

export default HistoryModal;
